import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Wrench, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function formatErr(detail) {
  if (detail == null) return "Registration failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

const Field = ({ id, label, children }) => (
  <div><Label htmlFor={id}>{label}</Label>{children}</div>
);

const Register = () => {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", password: "", company: "", tax_id: "", phone: "", line1: "", city: "", postal_code: "", country: "Canada" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await register({
        name: f.name, email: f.email, password: f.password, company: f.company,
        tax_id: f.tax_id, phone: f.phone,
        address: { line1: f.line1, city: f.city, postal_code: f.postal_code, country: f.country },
      });
      navigate("/account");
    } catch (err) { setError(formatErr(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-surface py-10 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="flex items-center justify-center gap-2 mb-6"><div className="w-9 h-9 rounded-md bg-graphite grid place-items-center"><Wrench className="w-5 h-5 text-orange" /></div><span className="font-extrabold tracking-tight text-graphite text-lg">GOT PARTS</span></Link>
        <form onSubmit={submit} data-testid="register-form" className="bg-white border border-border rounded-lg p-6 sm:p-8">
          <h1 className="text-2xl font-bold tracking-tight text-graphite">Create your business account</h1>
          <p className="text-sm text-steel-light mt-1 mb-6">B2B account for ordering and quotes.</p>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="name" label="Full name"><Input id="name" data-testid="reg-name" value={f.name} onChange={set("name")} className="mt-1.5 h-11" required /></Field>
              <Field id="company" label="Company"><Input id="company" data-testid="reg-company" value={f.company} onChange={set("company")} className="mt-1.5 h-11" /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="email" label="Email"><Input id="email" data-testid="reg-email" type="email" value={f.email} onChange={set("email")} className="mt-1.5 h-11" required /></Field>
              <Field id="password" label="Password (min 6)"><Input id="password" data-testid="reg-password" type="password" value={f.password} onChange={set("password")} className="mt-1.5 h-11" minLength={6} required /></Field>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field id="tax_id" label="Tax ID / NIF"><Input id="tax_id" data-testid="reg-tax" value={f.tax_id} onChange={set("tax_id")} className="mt-1.5 h-11" /></Field>
              <Field id="phone" label="Phone"><Input id="phone" data-testid="reg-phone" value={f.phone} onChange={set("phone")} className="mt-1.5 h-11" /></Field>
            </div>
            <div className="pt-2 mono text-xs uppercase tracking-[0.15em] text-steel-light">Delivery Address</div>
            <Field id="line1" label="Address"><Input id="line1" data-testid="reg-line1" value={f.line1} onChange={set("line1")} className="mt-1.5 h-11" /></Field>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field id="city" label="City"><Input id="city" data-testid="reg-city" value={f.city} onChange={set("city")} className="mt-1.5 h-11" /></Field>
              <Field id="postal_code" label="Postal code"><Input id="postal_code" data-testid="reg-postal" value={f.postal_code} onChange={set("postal_code")} className="mt-1.5 h-11" /></Field>
              <Field id="country" label="Country"><Input id="country" data-testid="reg-country" value={f.country} onChange={set("country")} className="mt-1.5 h-11" /></Field>
            </div>
            {error && <div data-testid="register-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
            <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full h-11 bg-orange hover:bg-orange-hover">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create Account"}</Button>
          </div>
          <p className="text-sm text-steel-light mt-5 text-center">Already registered? <Link to="/login" className="text-orange font-medium hover:underline">Sign in</Link></p>
        </form>
      </div>
    </div>
  );
};

export default Register;
